import React, { useEffect, useState } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { Button, Badge } from "./ui";
import { toast } from "sonner";
import { BASE_CODES } from "./CurrencyMasterDashboard";
import { n, fmt } from "../lib/equityMath";
import { Trash2 } from "lucide-react";
import { AppraisalButton } from "./Appraisal";
import { TaxForm } from "./EquityLedger";

// ---- shared tiny form primitives (mirrors EquityLedger styling) ----
const II = "w-full rounded border border-black/15 bg-white/70 px-1.5 py-1 text-[11px] tabular-nums dark:border-white/15 dark:bg-black/30";
const LL = "text-[9px] uppercase tracking-wide opacity-55";
const Chk = ({ label, checked, onChange, tid }) => (
  <label className="flex items-center gap-1 text-[10px]"><input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={tid} /> {label}</label>
);
const Num = ({ label, value, onChange, onBlur, tid }) => (
  <label className="block"><span className={LL}>{label}</span><input className={II} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} data-testid={tid} /></label>
);
const Txt = ({ label, value, onChange, onBlur, maxLength, tid }) => (
  <label className="block"><span className={LL}>{label}</span><input className={II} type="text" maxLength={maxLength} value={value ?? ""} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} data-testid={tid} /></label>
);
const PoolSel = ({ label, value, onChange, pools, tid }) => (
  <label className="block"><span className={LL}>{label}</span>
    <select className={II} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid}>
      <option value="">{TL("— account —")}</option>
      {pools.map((p) => <option key={p.id} value={p.id}>{p.name || p.id}{p.balance != null ? ` (${p.balance})` : ""}</option>)}
    </select>
  </label>
);
const CodeSel = ({ label, value, onChange, options, tid }) => (
  <label className="block"><span className={LL}>{label}</span>
    <select className={II} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid}>
      <option value="">{TL("— cur —")}</option>
      {options.map((c) => <option key={c} value={c}>{c}</option>)}
    </select>
  </label>
);
const RunBtn = ({ label, onClick, tid }) => (
  <button type="button" onClick={onClick} data-testid={tid} className="rounded bg-[var(--primary)] px-2 py-0.5 text-[10px] font-semibold text-white hover:opacity-90">{label}</button>
);
const YesNo = ({ label, value, onChange, tid }) => (
  <div className="rounded border border-black/10 bg-white/50 px-2 py-1 dark:border-white/10 dark:bg-black/20">
    <div className={LL}>{label}</div>
    <div className="mt-0.5 flex gap-3">
      {[["yes", "Yes"], ["no", "No"]].map(([k, l]) => (
        <label key={k} className="flex items-center gap-1 text-[10px]">
          <input type="radio" checked={(value ? "yes" : "no") === k} onChange={() => onChange(k === "yes")} data-testid={`${tid}-${k}`} /> {l}
        </label>
      ))}
    </div>
  </div>
);

const SHORT_DURS = ["30", "60", "90", "180", "360"];
const LONG_DURS = ["30", "60", "90", "180", "360", "2yr", "3yr", "4yr", "5yr", "7yr", "10yr", "__free__", "20yr", "30yr"];

const termToYears = (t) => {
  if (!t && t !== 0) return 0;
  const s = String(t).trim().toLowerCase();
  let m = s.match(/^(\d+(?:\.\d+)?)\s*yr$/); if (m) return parseFloat(m[1]);
  m = s.match(/^(\d+(?:\.\d+)?)$/); if (m) return parseFloat(m[1]) / 360; // bare number = days
  m = s.match(/(\d+(?:\.\d+)?)\s*(?:y|yr|year)/); if (m) return parseFloat(m[1]);
  m = s.match(/(\d+(?:\.\d+)?)\s*(?:d|day)/); if (m) return parseFloat(m[1]) / 360;
  const num = parseFloat(s); return isNaN(num) ? 0 : num / 360;
};
const noteVamBase = (nt) => {
  const p = n(nt.principal), r = n(nt.principal_rate);
  if (!p) return 0;
  if (!r || r >= 100) return p;
  return p / (1 - r / 100);
};
const noteVam = (nt) => {
  const base = noteVamBase(nt);
  if (!nt.accrue) return base;
  const yrs = termToYears(nt.term), r = n(nt.coupon_rate) / 100;
  if (!yrs || !r) return base;
  if (!nt.capitalize) return base + base * r * yrs;
  const inc = nt.income || {};
  const m = inc.freq_weekly ? 52 : inc.freq_quarterly ? 4 : 1;
  return base + base * (Math.pow(1 + r / m, m * yrs) - 1);
};
const noteMaturity = (nt) => {
  let start = nt.issue_date;
  if (!start) { const hist = (nt.buy_history || []).map((h) => h.date).filter(Boolean).sort(); start = hist[0]; }
  if (!start) return null;
  const d = new Date(start);
  if (isNaN(d.getTime())) return null;
  const t = String(nt.term || "").trim().toLowerCase();
  if (!t) return null;
  const ym = t.match(/^(\d+(?:\.\d+)?)\s*yr$/) || t.match(/(\d+(?:\.\d+)?)\s*(?:y|yr|year)/);
  if (ym) {
    const yy = parseFloat(ym[1]), whole = Math.floor(yy);
    d.setFullYear(d.getFullYear() + whole);
    if (yy - whole) d.setDate(d.getDate() + Math.round((yy - whole) * 365));
    return d;
  }
  const dm = t.match(/^(\d+(?:\.\d+)?)$/) || t.match(/(\d+(?:\.\d+)?)\s*(?:d|day)/);
  if (!dm) return null;
  d.setDate(d.getDate() + Math.round(parseFloat(dm[1]))); // day-based terms are exact
  return d;
};
const isLongNote = (nt) => /long/i.test(nt.subcategory || "") || nt.note_type === "long";

const blankNote = (type, sector, extra = {}) => ({
  note_type: type, category_label: "", ticker: "", principal: "", currency: "", custodian: "",
  term: "", issue_date: "", coupon_rate: "", principal_rate: "", renew: false, capitalize: false, accrue: false,
  sell_value: "", sell_currency: "", buy_history: [],
  income: {}, tax: {}, category: "Bonds and Notes", subcategory: sector, asset_class: "Fixed income",
  ...extra,
});

// Coupon-interest box — re-hash of the Equities Income box (dividend -> coupon interest).
function CouponIncomeForm({ nid, note, principal, cur, val, set, commit, pools, adjust }) {
  const interestAmt = n(principal) * (n(val.coupon_rate) / 100);
  const pfx = cur ? `${cur} ` : "";
  return (
    <div className="grid grid-cols-3 gap-2" data-testid={`note-inc-${nid}`}>
      <div className="space-y-1">
        <div className="text-[10px] font-semibold">{TL("Coupon interest")}</div>
        <Chk label={TL("Cash")} checked={val.cash} onChange={(v) => set("cash", v)} tid={`note-inc-cash-${nid}`} />
        <Chk label={TL("Reinvest")} checked={val.reinvest} onChange={(v) => set("reinvest", v)} tid={`note-inc-reinvest-${nid}`} />
        <div className="pt-1 flex flex-col gap-0.5">
          <span className={LL}>{TL("Mode")}</span>
          {["manual", "automated", "rule"].map((m) => (
            <label key={m} className="flex items-center gap-1 text-[10px] capitalize"><input type="radio" checked={val.mode === m} onChange={() => set("mode", m)} data-testid={`note-inc-mode-${nid}-${m}`} /> {m}</label>
          ))}
        </div>
      </div>
      <div className="space-y-1">
        <Num label={TL("Coupon interest rate (%)")} value={val.coupon_rate} onChange={(v) => set("coupon_rate", v, false)} onBlur={commit} tid={`note-inc-coupon-${nid}`} />
        <Chk label={TL("Weekly")} checked={val.freq_weekly} onChange={(v) => set("freq_weekly", v)} tid={`note-inc-weekly-${nid}`} />
        <Chk label={TL("Quarterly")} checked={val.freq_quarterly} onChange={(v) => set("freq_quarterly", v)} tid={`note-inc-quarterly-${nid}`} />
        <Chk label={TL("Annually")} checked={val.freq_annually} onChange={(v) => set("freq_annually", v)} tid={`note-inc-annually-${nid}`} />
      </div>
      <div className="space-y-1">
        <div><span className={LL}>{TL("Annual interest")}</span><div className="text-[10px] font-semibold tabular-nums" data-testid={`note-inc-amount-${nid}`}>{pfx}{fmt(interestAmt)}</div></div>
        <PoolSel label={TL("Pay to account")} value={val.pay_to_account} onChange={(v) => set("pay_to_account", v)} pools={pools} tid={`note-inc-payto-${nid}`} />
        <Num label={TL("YTD total")} value={val.ytd_total} onChange={(v) => set("ytd_total", v, false)} onBlur={commit} tid={`note-inc-ytd-${nid}`} />
        <RunBtn label={`Pay interest (${pfx}${fmt(interestAmt)})`} onClick={() => adjust(val.pay_to_account, "credit", interestAmt, "note_interest", { ticker: note.ticker })} tid={`note-inc-paynow-${nid}`} />
      </div>
    </div>
  );
}

function NoteCard({ note, durations, form, setField, setGroup, commitGroup, pools, codes, adjust, onDel, checked, onToggleSelect, reload }) {
  const f = form || {};
  const isPreset = durations.includes(f.term);
  const income = f.income || {};
  const tax = f.tax || {};
  const cur = f.currency || "";
  const vamBase = (() => {
    const p = n(f.principal), r = n(f.principal_rate);
    if (!p) return 0;
    if (!r || r >= 100) return p;
    return p / (1 - r / 100); // discount note accretes from discounted principal to par at maturity
  })();
  const accrual = (() => {
    if (!f.accrue || !vamBase) return 0;
    const yrs = termToYears(f.term), r = n(f.coupon_rate) / 100;
    if (!yrs || !r) return 0;
    if (!f.capitalize) return vamBase * r * yrs; // simple interest
    const inc = f.income || {};
    const m = inc.freq_weekly ? 52 : inc.freq_quarterly ? 4 : 1; // compounding periods/yr from coupon frequency (default annually)
    return vamBase * (Math.pow(1 + r / m, m * yrs) - 1);
  })();
  const vam = vamBase + accrual;
  const matures = noteMaturity({ issue_date: f.issue_date, term: f.term, buy_history: f.buy_history });
  const freqLabel = (() => { const i = f.income || {}; return i.freq_weekly ? "weekly" : i.freq_quarterly ? "quarterly" : "annually"; })();
  return (
    <div className="rounded-lg border border-black/10 bg-white/60 p-3 dark:border-white/10 dark:bg-white/[0.03]" data-testid={`note-card-${note.id}`}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <input type="checkbox" checked={!!checked} onChange={() => onToggleSelect(note.id)} data-testid={`note-select-${note.id}`} className="cursor-pointer accent-[var(--primary)]" title={TL("Select for bulk delete")} />
        <Badge color={note.note_type === "long" ? "info" : "neutral"}>{note.note_type === "long" ? "Long Note" : "Short Note"}</Badge>
        <span className="font-head text-sm" data-testid={`note-ticker-${note.id}`}>{f.ticker || "—"}</span>
        <button onClick={() => onDel(note)} data-testid={`note-del-${note.id}`} className="ms-auto opacity-60 hover:text-red-500 hover:opacity-100"><Trash2 size={13} /></button>
      </div>
      <div className="mb-2 flex justify-end"><AppraisalButton holding={note} reload={reload} /></div>

      {/* Top row: Category / Ticker / Principal / Custodian / Currency */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-5" data-testid={`note-top-${note.id}`}>
        <Txt label={TL("Category (15)")} maxLength={15} value={f.category_label} onChange={(v) => setField("category_label", v, false)} onBlur={() => setField("category_label", f.category_label, true)} tid={`note-f-category-${note.id}`} />
        <Txt label={TL("Ticker (6)")} maxLength={6} value={f.ticker} onChange={(v) => setField("ticker", v, false)} onBlur={() => setField("ticker", f.ticker, true)} tid={`note-f-ticker-${note.id}`} />
        <Num label={TL("Principal (15)")} value={f.principal} onChange={(v) => setField("principal", v, false)} onBlur={() => setField("principal", f.principal, true)} tid={`note-f-principal-${note.id}`} />
        <Txt label={TL("Custodian (50)")} maxLength={50} value={f.custodian} onChange={(v) => setField("custodian", v, false)} onBlur={() => setField("custodian", f.custodian, true)} tid={`note-f-custodian-${note.id}`} />
        <CodeSel label={TL("Currency")} value={f.currency} onChange={(v) => setField("currency", v, true)} options={codes} tid={`note-f-currency-${note.id}`} />
      </div>
      {(f.buy_history && f.buy_history.length > 0) && (
        <div className="mt-1 text-[9px] opacity-60" data-testid={`note-buyhist-${note.id}`}>
          Buys: {f.buy_history.map((h) => `${h.date} +${fmt(h.amount)}${h.currency ? ` ${h.currency}` : ""}`).join(" · ")}
        </div>
      )}

      {/* Durations + rates + toggles */}
      <div className="mt-2 grid gap-3 lg:grid-cols-[1fr_auto]">
        <div className="rounded border border-black/10 bg-white/40 px-2 py-2 dark:border-white/10 dark:bg-black/10" data-testid={`note-terms-${note.id}`}>
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1 text-[10px]"><span className={LL}>{TL("Issue date")}</span>
              <input type="date" value={f.issue_date || ""} onChange={(e) => setField("issue_date", e.target.value, true)} data-testid={`note-f-issuedate-${note.id}`}
                className="rounded border border-black/15 bg-white/70 px-1 py-0.5 text-[10px] dark:border-white/15 dark:bg-black/30" /></label>
            {matures && <span className="text-[9px] font-medium opacity-70" data-testid={`note-matures-${note.id}`}>Matures {matures.toISOString().slice(0, 10)}</span>}
          </div>
          <div className={LL}>{TL("Term duration (days / years) — pick one")}</div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
            {durations.map((d) => d === "__free__" ? (
              <label key={d} className="flex items-center gap-1 text-[10px]">
                <span className="opacity-55">{TL("Custom:")}</span>
                <input type="text" maxLength={12} value={isPreset ? "" : (f.term || "")} onChange={(e) => setField("term", e.target.value, false)} onBlur={() => setField("term", f.term, true)}
                  data-testid={`note-term-custom-${note.id}`} className="w-20 rounded border border-black/15 bg-white/70 px-1 py-0.5 text-[10px] dark:border-white/15 dark:bg-black/30" placeholder={TL("free form")} />
              </label>
            ) : (
              <label key={d} className="flex items-center gap-1 text-[10px]">
                <input type="checkbox" checked={f.term === d} onChange={() => setField("term", f.term === d ? "" : d, true)} data-testid={`note-term-${note.id}-${d}`} /> {d}
              </label>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-start gap-2">
          <div className="w-28"><Num label={TL("Coupon rate (%)")} value={f.coupon_rate} onChange={(v) => setField("coupon_rate", v, false)} onBlur={() => setField("coupon_rate", f.coupon_rate, true)} tid={`note-f-coupon-${note.id}`} /></div>
          <div className="w-32"><Num label={TL("Principal rate (discount %)")} value={f.principal_rate} onChange={(v) => setField("principal_rate", v, false)} onBlur={() => setField("principal_rate", f.principal_rate, true)} tid={`note-f-principal-rate-${note.id}`} /></div>
          <div className="rounded border border-black/10 px-2 py-1 dark:border-white/10" data-testid={`note-sell-${note.id}`}>
            <div className={LL}>{TL("Sell (value / cur to)")}</div>
            <div className="mt-0.5 flex items-end gap-1">
              <div className="w-20"><input type="number" className={II} value={f.sell_value ?? ""} onChange={(e) => setField("sell_value", e.target.value, false)} onBlur={() => setField("sell_value", f.sell_value, true)} data-testid={`note-f-sell-${note.id}`} /></div>
              <div className="w-16"><select className={II} value={f.sell_currency || ""} onChange={(e) => setField("sell_currency", e.target.value, true)} data-testid={`note-f-sellcur-${note.id}`}>
                <option value="">{TL("cur")}</option>{codes.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></div>
            </div>
          </div>
          <div className="rounded border border-emerald-500/30 bg-emerald-500/5 px-2 py-1" title={f.accrue ? `Principal accretion + coupon accrual over term (${f.capitalize ? `compounded ${freqLabel}` : "simple"})` : "Principal accretion to par at maturity"}>
            <div className={LL}>{TL("Value at maturity")}</div>
            <div className="mt-0.5 text-[12px] font-semibold tabular-nums text-emerald-700 dark:text-emerald-400" data-testid={`note-vam-${note.id}`}>{cur ? `${cur} ` : ""}{fmt(vam)}</div>
            {f.accrue && accrual > 0 && <div className="text-[9px] opacity-60" data-testid={`note-vam-accrual-${note.id}`}>incl. coupon {cur ? `${cur} ` : ""}{fmt(accrual)}</div>}
          </div>
          <YesNo label={TL("Accrue coupon")} value={f.accrue} onChange={(v) => setField("accrue", v, true)} tid={`note-f-accrue-${note.id}`} />
          <YesNo label={TL("Renew Y/Y")} value={f.renew} onChange={(v) => setField("renew", v, true)} tid={`note-f-renew-${note.id}`} />
          <YesNo label={TL("Capitalize Y/N")} value={f.capitalize} onChange={(v) => setField("capitalize", v, true)} tid={`note-f-capitalize-${note.id}`} />
        </div>
      </div>

      {/* Income (coupon interest) + Tax & Commissions */}
      <div className="mt-3 grid gap-3 xl:grid-cols-2">
        <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2">
          <CouponIncomeForm nid={note.id} note={note} principal={f.principal} cur={cur}
            val={income} set={(k, v, p) => setGroup("income", k, v, p)} commit={() => commitGroup("income")}
            pools={pools} adjust={adjust} />
        </div>
        <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2">
          <div className="mb-1 text-[10px] font-semibold">{TL("Tax and Commissions")}</div>
          <TaxForm pid={note.id} val={tax} set={(k, v, p) => setGroup("tax", k, v, p)} commit={() => commitGroup("tax")}
            pools={pools} codes={codes} adjust={adjust} />
        </div>
      </div>
    </div>
  );
}

export default function NoteLedger({ sector, notes = [], reload, scope = null, forceType = null }) {
  const isLong = forceType ? forceType === "long" : /long/i.test(sector || "");
  const durations = isLong ? LONG_DURS : SHORT_DURS;
  const noteType = isLong ? "long" : "short";
  const codes = [...new Set([...BASE_CODES])];

  const [pools, setPools] = useState([]);
  const [forms, setForms] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const emptyTpl = { bond_type: "", ticker: "", custodian: "", issue_date: "", from_cur: "", buy_amt: "", to_cur: "", sell_amt: "" };
  const [tpl, setTpl] = useState(emptyTpl);
  const [mode, setMode] = useState("new");
  const [existingId, setExistingId] = useState("");
  const setT = (k, v) => setTpl((p) => ({ ...p, [k]: v }));

  const loadPools = () => client.get("/currencies").then((r) => setPools((r.data || []).map((x) => ({ id: x.id, ...(x.data || x) })))).catch(() => {});
  useEffect(() => { loadPools(); }, []);
  useEffect(() => {
    setForms((prev) => {
      const next = { ...prev };
      notes.forEach((nt) => {
        if (!next[nt.id]) {
          next[nt.id] = {
            category_label: nt.category_label || "", ticker: nt.ticker || "", principal: nt.principal ?? "",
            currency: nt.currency || "", custodian: nt.custodian || "", term: nt.term || "", issue_date: nt.issue_date || "", coupon_rate: nt.coupon_rate ?? "",
            principal_rate: nt.principal_rate ?? "", renew: !!nt.renew, capitalize: !!nt.capitalize, accrue: !!nt.accrue,
            sell_value: nt.sell_value ?? "", sell_currency: nt.sell_currency || "", buy_history: nt.buy_history || [],
            income: nt.income || {}, tax: nt.tax || {},
          };
        }
      });
      return next;
    });
    setSelected((prev) => {
      const ids = new Set(notes.map((nt) => nt.id));
      const nx = new Set([...prev].filter((id) => ids.has(id)));
      return nx.size === prev.size ? prev : nx;
    });
  }, [notes]);

  const setField = (id, key, value, persist) => {
    setForms((prev) => {
      const cur = { ...(prev[id] || {}), [key]: value };
      if (persist) client.put(`/holdings/${id}`, { data: { [key]: value } }).catch(() => {});
      return { ...prev, [id]: cur };
    });
  };
  const setGroup = (id, group, key, value, persist) => {
    setForms((prev) => {
      const pf = prev[id] || {};
      const g = { ...(pf[group] || {}), [key]: value };
      if (persist) client.put(`/holdings/${id}`, { data: { [group]: g } }).catch(() => {});
      return { ...prev, [id]: { ...pf, [group]: g } };
    });
  };
  const commitGroup = (id, group) => setForms((prev) => { client.put(`/holdings/${id}`, { data: { [group]: prev[id]?.[group] || {} } }).catch(() => {}); return prev; });

  const adjust = async (poolId, direction, amount, reason, meta) => {
    if (!poolId) return toast.error("Pick a Currencida account first.");
    if (!(Number(amount) > 0)) return toast.error("Nothing to move — amount is zero.");
    try {
      const r = await client.post("/currency/adjust", { pool_id: poolId, direction, amount: Number(amount), reason, meta: meta || {} });
      toast.success(`${direction === "debit" ? "Debited" : "Credited"} ${fmt(amount)} · new balance ${fmt(r.data.balance)}.`);
      loadPools();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Transfer failed."); return false; }
  };

  const today = () => new Date().toISOString().slice(0, 10);
  const addFromTemplate = async (count) => {
    const buyAmt = n(tpl.buy_amt);
    const extra = {
      category_label: tpl.bond_type || "", ticker: tpl.ticker || "", custodian: tpl.custodian || "",
      currency: tpl.from_cur || "", principal: tpl.buy_amt || "", issue_date: tpl.issue_date || "",
      sell_value: tpl.sell_amt || "", sell_currency: tpl.to_cur || "",
      buy_history: buyAmt > 0 ? [{ date: today(), amount: buyAmt, currency: tpl.from_cur || "" }] : [],
    };
    try {
      for (let i = 0; i < count; i++) await client.post("/holdings", { data: blankNote(noteType, sector, { ...extra, ...(scope || {}) }) });
      const templated = tpl.ticker || tpl.bond_type || buyAmt > 0;
      toast.success(`Added ${count} ${noteType === "long" ? "Long" : "Short"} Note${count > 1 ? "s" : ""}${templated ? " from template" : ""}.`);
      reload && reload();
    } catch (e) { toast.error("Could not add slots."); }
  };
  const addToExisting = async () => {
    if (!existingId) return toast.error("Pick an existing note to add to.");
    const buyAmt = n(tpl.buy_amt);
    if (!(buyAmt > 0)) return toast.error("Enter a Buy amount to add.");
    const nt = notes.find((x) => x.id === existingId);
    const cur = forms[existingId] || nt || {};
    const newPrincipal = n(cur.principal) + buyAmt;
    const hist = [...(cur.buy_history || []), { date: today(), amount: buyAmt, currency: tpl.from_cur || cur.currency || "" }];
    try {
      await client.put(`/holdings/${existingId}`, { data: { principal: newPrincipal, buy_history: hist } });
      setForms((prev) => ({ ...prev, [existingId]: { ...(prev[existingId] || {}), principal: newPrincipal, buy_history: hist } }));
      toast.success(`Added ${fmt(buyAmt)} to ${label(nt)} — principal now ${fmt(newPrincipal)}.`);
      reload && reload();
    } catch (e) { toast.error("Could not add to record."); }
  };
  const toggleSelect = (id) => setSelected((prev) => { const n2 = new Set(prev); n2.has(id) ? n2.delete(id) : n2.add(id); return n2; });
  const label = (nt) => (forms[nt.id]?.ticker || forms[nt.id]?.category_label || nt.ticker || "(blank)");
  const isEmpty = (nt) => {
    const f = forms[nt.id] || nt;
    return !String(f.ticker || "").trim() && !String(f.category_label || "").trim() && !String(f.principal ?? "").toString().trim();
  };
  const emptySlots = notes.filter(isEmpty);
  const tplTk = (tpl.ticker || "").trim().toLowerCase();
  const tplCust = (tpl.custodian || "").trim().toLowerCase();
  const matchExisting = (nt) => {
    const f = forms[nt.id] || nt;
    const tk = String(f.ticker || "").trim().toLowerCase();
    const cust = String(f.custodian || "").trim().toLowerCase();
    if (!tplTk && !tplCust) return true;
    return (tplTk && tk === tplTk) || (tplCust && cust === tplCust);
  };
  const existingOptions = notes.filter(matchExisting);
  const optLabel = (nt) => {
    const f = forms[nt.id] || nt;
    const bits = [f.ticker || f.category_label || "(blank)"];
    if (f.custodian) bits.push(f.custodian);
    if (f.principal !== "" && f.principal != null) bits.push(`${f.currency ? f.currency + " " : ""}${fmt(n(f.principal))}`);
    return bits.join(" · ");
  };
  useEffect(() => {
    if (mode !== "existing") return;
    if (existingOptions.length === 1) setExistingId(existingOptions[0].id);
    else if (existingId && !existingOptions.some((nt) => nt.id === existingId)) setExistingId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, tplTk, tplCust, notes]);
  const delNotes = async (list, verb) => {
    if (!list.length) return;
    const names = list.map(label).slice(0, 12).join(", ") + (list.length > 12 ? "…" : "");
    if (!window.confirm(`${verb} ${list.length} note${list.length > 1 ? "s" : ""}?\n\n${names}\n\nThis cannot be undone.`)) return;
    try {
      await Promise.all(list.map((nt) => client.delete(`/holdings/${nt.id}`)));
      setSelected(new Set());
      toast.success(`Deleted ${list.length} note${list.length > 1 ? "s" : ""}.`);
      reload && reload();
    } catch (e) { toast.error("Delete failed."); }
  };
  const delSelected = () => delNotes(notes.filter((nt) => selected.has(nt.id)), "Delete");
  const removeEmpty = () => delNotes(emptySlots, "Remove");
  const delOne = (nt) => delNotes([nt], "Delete");

  const btn = "px-2 py-1 text-[11px]";
  return (
    <div className="space-y-2" data-testid={`note-ledger-${sector}`}>
      <div className="ps-1 text-[10px] italic opacity-50" data-testid={`note-caption-${sector}`}>{isLong ? "over one year" : "up to 1 year"}</div>
      <div className="rounded-lg border border-black/10 bg-black/[0.02] p-2 dark:border-white/10 dark:bg-white/[0.03]" data-testid={`note-quickadd-${sector}`}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">Quick add — {noteType === "long" ? "Long" : "Short"} Note</span>
          <div className="flex gap-3 text-[10px]">
            {[["new", "New record"], ["existing", "Add to existing"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-1"><input type="radio" checked={mode === k} onChange={() => setMode(k)} data-testid={`note-mode-${sector}-${k}`} /> {l}</label>
            ))}
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <div className="w-24"><Txt label={TL("Bond type")} maxLength={15} value={tpl.bond_type} onChange={(v) => setT("bond_type", v)} tid={`note-tpl-bondtype-${sector}`} /></div>
          <div className="w-16"><Txt label={TL("Ticker")} maxLength={6} value={tpl.ticker} onChange={(v) => setT("ticker", v)} tid={`note-tpl-ticker-${sector}`} /></div>
          <div className="w-28"><Txt label={TL("Custodian")} maxLength={50} value={tpl.custodian} onChange={(v) => setT("custodian", v)} tid={`note-tpl-custodian-${sector}`} /></div>
          <label className="block"><span className={LL}>{TL("Issue date")}</span>
            <input type="date" value={tpl.issue_date} onChange={(e) => setT("issue_date", e.target.value)} data-testid={`note-tpl-issuedate-${sector}`}
              className="rounded border border-black/15 bg-white/70 px-1.5 py-1 text-[11px] dark:border-white/15 dark:bg-black/30" /></label>
          <div className="flex items-end gap-1 rounded border border-black/10 px-1.5 py-1 dark:border-white/10">
            <div className="w-16"><CodeSel label={TL("Cur from")} value={tpl.from_cur} onChange={(v) => setT("from_cur", v)} options={codes} tid={`note-tpl-fromcur-${sector}`} /></div>
            <div className="w-20"><Num label={TL("Buy")} value={tpl.buy_amt} onChange={(v) => setT("buy_amt", v)} tid={`note-tpl-buy-${sector}`} /></div>
          </div>
          <div className="flex items-end gap-1 rounded border border-black/10 px-1.5 py-1 dark:border-white/10">
            <div className="w-16"><CodeSel label={TL("Cur to")} value={tpl.to_cur} onChange={(v) => setT("to_cur", v)} options={codes} tid={`note-tpl-tocur-${sector}`} /></div>
            <div className="w-20"><Num label={TL("Sell")} value={tpl.sell_amt} onChange={(v) => setT("sell_amt", v)} tid={`note-tpl-sell-${sector}`} /></div>
          </div>
          {mode === "new" ? (
            <div className="flex items-end gap-1">
              <span className={LL}>{TL("Add")}</span>
              <div className="flex overflow-hidden rounded border border-black/15 text-[11px] dark:border-white/15">
                {[1, 5, 10].map((c) => (
                  <button key={c} onClick={() => addFromTemplate(c)} data-testid={`note-add-${sector}-${c}`} className={`${btn} font-semibold hover:bg-[var(--primary)] hover:text-white`} title={`Add ${c} line${c > 1 ? "s" : ""} from template`}>+{c}</button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-end gap-1">
              <div className="w-52"><label className="block"><span className={LL}>Existing record{(tplTk || tplCust) ? ` · matching ${tpl.ticker || tpl.custodian} (${existingOptions.length})` : ""}</span>
                <select value={existingId} onChange={(e) => setExistingId(e.target.value)} data-testid={`note-existing-${sector}`} className="w-full rounded border border-black/15 bg-white/70 px-1.5 py-1 text-[11px] dark:border-white/15 dark:bg-black/30">
                  <option value="">{TL("— select —")}</option>
                  {existingOptions.map((nt) => <option key={nt.id} value={nt.id}>{optLabel(nt)}</option>)}
                </select></label></div>
              <button onClick={addToExisting} data-testid={`note-addto-${sector}`} className="rounded bg-[var(--primary)] px-2 py-1 text-[11px] font-semibold text-white hover:opacity-90">{TL("Add to record")}</button>
            </div>
          )}
        </div>
      </div>

      {notes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-[11px]" data-testid={`note-bulk-${sector}`}>
          <label className="inline-flex cursor-pointer items-center gap-1.5 opacity-70 hover:opacity-100">
            <input type="checkbox" data-testid={`note-select-all-${sector}`}
              checked={selected.size === notes.length && notes.length > 0}
              ref={(el) => { if (el) el.indeterminate = selected.size > 0 && selected.size < notes.length; }}
              onChange={(e) => setSelected(e.target.checked ? new Set(notes.map((nt) => nt.id)) : new Set())} />
            Select all
          </label>
          {selected.size > 0 && (
            <button onClick={delSelected} data-testid={`note-del-selected-${sector}`} className="inline-flex items-center gap-1 rounded border border-red-500/40 px-2 py-1 font-semibold text-red-600 hover:bg-red-500/10 dark:text-red-400">
              <Trash2 size={12} /> Delete selected ({selected.size})
            </button>
          )}
          {emptySlots.length > 0 && (
            <button onClick={removeEmpty} data-testid={`note-remove-empty-${sector}`} className="inline-flex items-center gap-1 rounded border border-black/15 px-2 py-1 opacity-70 hover:opacity-100 dark:border-white/15">
              <Trash2 size={12} /> Remove empty slots ({emptySlots.length})
            </button>
          )}
        </div>
      )}

      {notes.length === 0 ? (
        <div className="rounded-lg border border-dashed border-black/15 py-6 text-center text-xs opacity-60 dark:border-white/15" data-testid={`note-empty-${sector}`}>
          No {noteType === "long" ? "Long" : "Short"} Notes in “{sector}” yet. Use “Add … +1 / +5 / +10” to create slots, then fill in the details inline.
        </div>
      ) : (
        <div className="space-y-2">
          {notes.map((nt) => (
            <NoteCard key={nt.id} note={{ ...nt, note_type: nt.note_type || noteType }} durations={durations}
              form={forms[nt.id]} pools={pools} codes={codes} adjust={adjust}
              setField={(k, v, p) => setField(nt.id, k, v, p)} setGroup={(g, k, v, p) => setGroup(nt.id, g, k, v, p)} commitGroup={(g) => commitGroup(nt.id, g)}
              onDel={delOne} checked={selected.has(nt.id)} onToggleSelect={toggleSelect} reload={reload} />
          ))}
        </div>
      )}
    </div>
  );
}


function NoteDash({ title, group, testid }) {
  const value = group.reduce((a, nt) => a + n(nt.principal), 0);
  const vam = group.reduce((a, nt) => a + noteVam(nt), 0);
  const curs = [...new Set(group.map((nt) => nt.currency).filter(Boolean))];
  const pfx = curs.length === 1 ? `${curs[0]} ` : "";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  let overdue = 0, soon = 0, upcoming = 0, tracked = 0;
  group.forEach((nt) => {
    const m = noteMaturity(nt);
    if (!m) return;
    tracked++;
    const days = Math.round((m - today) / 86400000);
    if (days < 0) overdue++; else if (days <= 30) soon++; else if (days <= 90) upcoming++;
  });
  return (
    <div className="rounded-lg border border-black/10 bg-white/60 p-3 dark:border-white/10 dark:bg-white/[0.03]" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-wide opacity-60">{title}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums" data-testid={`${testid}-value`}>{pfx}{fmt(value)}</div>
      <div className="text-[10px] opacity-60">{group.length} note{group.length === 1 ? "" : "s"} · at maturity {pfx}{fmt(vam)}</div>
      <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`${testid}-alerts`}>
        {overdue > 0 && <Badge color="danger">{overdue} matured</Badge>}
        {soon > 0 && <Badge color="warning">{soon} ≤30d</Badge>}
        {upcoming > 0 && <Badge color="info">{upcoming} ≤90d</Badge>}
        {tracked === 0 && <span className="text-[9px] opacity-50">{TL("no maturities tracked")}</span>}
        {tracked > 0 && overdue + soon + upcoming === 0 && <span className="text-[9px] opacity-50">{TL("none due soon")}</span>}
      </div>
    </div>
  );
}

export function NotesCategorySummary({ notes = [] }) {
  const longs = notes.filter(isLongNote);
  const shorts = notes.filter((nt) => !isLongNote(nt));
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" data-testid="notes-category-summary">
      <NoteDash title={TL("Total Short Notes Value")} group={shorts} testid="notes-dash-short" />
      <NoteDash title={TL("Total All Notes Value")} group={notes} testid="notes-dash-all" />
      <NoteDash title={TL("Total Long Notes Value")} group={longs} testid="notes-dash-long" />
    </div>
  );
}
